import React, { useCallback, useEffect, useState } from 'react';
import CollapsibleSection from '../../CollapsibleSection';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import {
  SessionTiming,
  formatDuration,
  getOpenSession,
  getRecentSessions,
  startSession,
  stopSession,
} from '../../../services/SessionTimingService';

interface SessionTimerCardProps {
  patients: Array<{ id: string; name: string }>;
  practiceOwnerId?: string;
}

/**
 * Start/Stop timer for phone and home sessions. Saves only the start time, end
 * time and a patient reference. The running session is stored on the server, so
 * a refresh, a locked phone or a dropped connection does not lose it.
 */
const SessionTimerCard: React.FC<SessionTimerCardProps> = ({ patients, practiceOwnerId }) => {
  const { user } = useAuth();
  const toast = useToast();
  const uid = user?.uid || '';

  const [open, setOpen] = useState<SessionTiming | null>(null);
  const [recent, setRecent] = useState<SessionTiming[]>([]);
  const [patientId, setPatientId] = useState('');
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);

  const nameFor = (id: string) => patients.find((p) => p.id === id)?.name || (id ? 'Patient' : 'No patient linked');

  const refresh = useCallback(async () => {
    if (!uid) return;
    try {
      setOpen(await getOpenSession(uid));
      setRecent(await getRecentSessions(uid));
    } catch (err) {
      console.error('Session timer load failed:', err);
    }
  }, [uid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [open]);

  const handleStart = async () => {
    setBusy(true);
    try {
      await startSession(uid, practiceOwnerId || uid, patientId);
      await refresh();
    } catch (err: any) {
      toast.error('Could not start the timer', err.message || 'Something went wrong.');
    }
    setBusy(false);
  };

  const handleStop = async () => {
    if (!open) return;
    setBusy(true);
    try {
      await stopSession(open.id);
      toast.success('Session saved', 'The start time, end time and length were recorded.');
      await refresh();
    } catch (err: any) {
      toast.error('Could not stop the timer', err.message || 'Something went wrong.');
    }
    setBusy(false);
  };

  const elapsed = open ? Math.max(0, Math.round((now - open.startedAt.getTime()) / 1000)) : 0;
  const btn: React.CSSProperties = { padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '1rem' };

  return (
    <CollapsibleSection title={open ? 'Session timer (running)' : 'Session timer'} variant={open ? 'success' : 'default'} defaultExpanded={!!open}>
      <p style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: 0 }}>
        For phone and home sessions. Only the times and who it was with are saved. Nothing is recorded, and only you and your director can see it.
      </p>

      {open ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '2rem', fontVariantNumeric: 'tabular-nums', color: '#10b981' }}>{formatDuration(elapsed)}</div>
            <div style={{ color: '#d1d5db', fontSize: '0.85rem' }}>
              {nameFor(open.patientId)} · started {open.startedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
          </div>
          <button type="button" onClick={handleStop} disabled={busy} style={{ ...btn, background: '#ef4444', color: '#fff' }}>
            {busy ? 'Saving…' : 'Stop session'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            aria-label="Patient for this session"
            value={patientId}
            onChange={(e) => setPatientId(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '6px', minWidth: '12rem' }}
          >
            <option value="">No patient linked</option>
            {patients.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button type="button" onClick={handleStart} disabled={busy} style={{ ...btn, background: '#D4A017', color: '#111' }}>
            {busy ? 'Starting…' : 'Start session'}
          </button>
        </div>
      )}

      {recent.length > 0 && (
        <table style={{ width: '100%', marginTop: '1rem', borderCollapse: 'collapse', fontSize: '0.85rem', color: '#e5e7eb' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#9ca3af' }}>
              <th style={{ padding: '0.3rem' }}>Date</th>
              <th style={{ padding: '0.3rem' }}>Patient</th>
              <th style={{ padding: '0.3rem' }}>Start – End</th>
              <th style={{ padding: '0.3rem' }}>Length</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((s) => (
              <tr key={s.id} style={{ borderTop: '1px solid #374151' }}>
                <td style={{ padding: '0.3rem' }}>{s.startedAt.toLocaleDateString()}</td>
                <td style={{ padding: '0.3rem' }}>{nameFor(s.patientId)}</td>
                <td style={{ padding: '0.3rem' }}>
                  {s.startedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} – {s.endedAt?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </td>
                <td style={{ padding: '0.3rem' }}>{formatDuration(s.durationSeconds || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CollapsibleSection>
  );
};

export default SessionTimerCard;
